import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Circle, AlertTriangle } from 'lucide-react';
import { AssignmentDB } from '../../utils/assignmentsData';
import { mockUser } from '../../utils/mockData';
import { Button } from '../ui/button';
import { getStatusColor, getPriorityColor } from '../../utils/colorMap';
import { formatDate, formatDateTime } from '../../utils/formatters';

export default function MyAssignmentsView({ userId = null, viewType = 'assignedToMe' }) {
  const currentUserId = userId || mockUser.id;
  const [assignments, setAssignments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [workload, setWorkload] = useState(null);

  const loadAssignments = useCallback(() => {
    let list;
    if (viewType === 'assignedToMe') {
      list = AssignmentDB.getAssignmentsForUser(currentUserId, filterStatus);
    } else {
      list = AssignmentDB.getAssignmentsByUser(currentUserId, filterStatus);
    }

    if (filterPriority !== 'all') {
      list = list.filter(a => a.priority === filterPriority);
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(a =>
        (a.itemTitle && a.itemTitle.toLowerCase().includes(q)) ||
        (a.justification && a.justification.toLowerCase().includes(q)) ||
        (a.assignedByName && a.assignedByName.toLowerCase().includes(q)) ||
        (a.assignedToName && a.assignedToName.toLowerCase().includes(q))
      );
    }

    setAssignments(list);
  }, [currentUserId, viewType, filterStatus, filterPriority, searchTerm]);

  const loadWorkload = useCallback(() => {
    if (viewType === 'assignedToMe') {
      const w = AssignmentDB.getWorkloadForUser(currentUserId);
      setWorkload(w);
    } else {
      setWorkload(null);
    }
  }, [currentUserId, viewType]);

  useEffect(() => {
    loadAssignments();
    loadWorkload();
  }, [loadAssignments, loadWorkload]);

  const handleMarkComplete = (id) => {
    AssignmentDB.updateAssignmentStatus(id, 'completed');
    loadAssignments();
    loadWorkload();
  };

  const handleDecline = (id, reason) => {
    if (!reason || reason.trim() === '') {
      alert('Please provide a reason for declining this assignment.');
      return;
    }
    AssignmentDB.updateAssignmentStatus(id, 'declined', reason);
    loadAssignments();
    loadWorkload();
  };

  const activeCount = assignments.filter(a => a.status === 'active').length;
  const overdueCount = assignments.filter(a => {
    if (a.status !== 'active' || !a.dueDate) return false;
    return new Date(a.dueDate) < new Date();
  }).length;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="dashboard-title">
            {viewType === 'assignedToMe' ? 'My Assignments' : "Assignments I've Made"}
          </h1>
          <p className="dashboard-subtitle">
            {viewType === 'assignedToMe'
              ? 'Tasks and items assigned to you'
              : 'Tasks and items you have assigned to others'}
          </p>
        </div>
        {workload && (
          <div style={{
            padding: '12px 20px',
            background: workload.level === 'green' ? '#E8F5E9' : workload.level === 'yellow' ? '#FEF3C7' : workload.level === 'red' ? '#FEE2E2' : '#FEE2E2',
            borderRadius: 8,
            border: `2px solid ${workload.level === 'green' ? '#10B981' : workload.level === 'yellow' ? '#F59E0B' : workload.level === 'red' ? '#EF4444' : '#EF4444'}`
          }}>
            <div className={`text-body-sm font-semibold ${workload.level === 'green' ? 'text-green-nn' : workload.level === 'yellow' ? 'text-yellow-nn' : 'text-red-critical'}`} style={{ marginBottom: 4 }}>
              Current Workload
            </div>
            <div className={`text-xl font-bold ${workload.level === 'green' ? 'text-green-nn' : workload.level === 'yellow' ? 'text-yellow-nn' : 'text-red-critical'}`}>
              {workload.count} {workload.points > 0 ? `(${workload.points} pts)` : ''}{' '}
              {workload.level === 'critical' ? <AlertCircle size={16} color="#D94F4F" strokeWidth={2} style={{ verticalAlign: 'middle' }} /> : workload.level === 'red' ? <Circle size={12} fill="#D94F4F" color="#D94F4F" strokeWidth={2} style={{ verticalAlign: 'middle' }} /> : workload.level === 'yellow' ? <Circle size={12} fill="#E5A820" color="#E5A820" strokeWidth={2} style={{ verticalAlign: 'middle' }} /> : <Circle size={12} fill="#2EB87A" color="#2EB87A" strokeWidth={2} style={{ verticalAlign: 'middle' }} />}
            </div>
          </div>
        )}
      </div>

      {workload && workload.level !== 'green' && (
        <div style={{
          marginBottom: 24,
          padding: 16,
          background: workload.level === 'critical' ? '#FEE2E2' : '#FEF3C7',
          borderLeft: `4px solid ${workload.level === 'critical' ? '#EF4444' : '#F59E0B'}`,
          borderRadius: 6
        }}>
          <div className={`text-body font-semibold ${workload.level === 'critical' ? 'text-red-critical' : 'text-yellow-nn'}`} style={{ marginBottom: 4 }}>
            <>{workload.level === 'critical' ? <><AlertCircle size={16} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Critical Overload</> : <><AlertTriangle size={16} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 4 }} /> High Workload</>}</>
          </div>
          <div className={`text-body ${workload.level === 'critical' ? 'text-red-critical' : 'text-yellow-nn'}`}>
            You have {workload.count} active assignments. Consider requesting help or reassigning some tasks.
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-5" style={{ marginBottom: 24 }}>
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Active Assignments</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{activeCount}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">{assignments.length} total</div>
          </div>
        </div>
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-red-critical">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Overdue</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{overdueCount}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-red-critical">{overdueCount > 0 ? 'Action needed' : 'All on time'}</div>
          </div>
        </div>
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Completed</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{assignments.filter(a => a.status === 'completed').length}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">This period</div>
          </div>
        </div>
      </div>

      <div className="filter-panel bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: 24, padding: 20 }}>
        <div className="filter-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <label className="text-body font-medium" style={{ display: 'block', marginBottom: 5 }}>Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="declined">Declined</option>
            </select>
          </div>
          <div>
            <label className="text-body font-medium" style={{ display: 'block', marginBottom: 5 }}>Priority</label>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua">
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="text-body font-medium" style={{ display: 'block', marginBottom: 5 }}>Search</label>
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search assignments..." className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua" />
          </div>
        </div>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-16 px-5 text-text-dark-secondary bg-nn-white rounded-[14px] shadow-card overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
          <p className="text-text-muted" style={{ marginBottom: 20 }}>
            {filterStatus !== 'all' || filterPriority !== 'all' || searchTerm
              ? 'No assignments match your filters.'
              : viewType === 'assignedToMe'
                ? 'You have no assignments.'
                : "You haven't assigned any tasks yet."}
          </p>
        </div>
      ) : (
        <div className="initiatives-list" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {assignments.map(assignment => {
            const isOverdue = assignment.status === 'active' && assignment.dueDate && new Date(assignment.dueDate) < new Date();
            return (
              <div
                key={assignment.id}
                className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative"
                style={{
                  borderLeft: `4px solid ${getStatusColor(assignment.status)}`,
                  background: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div className="p-5">
                  <div className="initiative-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 15 }}>
                    <div style={{ flex: 1 }}>
                      <h3 className="text-lg font-medium" style={{ margin: '0 0 5px 0' }}>{assignment.itemTitle}</h3>
                      <div className="text-body text-text-muted">
                        {viewType === 'assignedToMe'
                          ? `Assigned by: ${assignment.assignedByName}`
                          : `Assigned to: ${assignment.assignedToName}`}
                        {assignment.dueDate && ` | Due: ${formatDate(assignment.dueDate)}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        background: getPriorityColor(assignment.priority) + '20',
                        color: getPriorityColor(assignment.priority),
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {assignment.priority.toUpperCase()}
                      </span>
                      <span style={{
                        background: getStatusColor(assignment.status) + '20',
                        color: getStatusColor(assignment.status),
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {assignment.status.toUpperCase()}
                      </span>
                      {isOverdue && (
                        <span style={{
                          background: '#EF444420',
                          color: '#EF4444',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                          OVERDUE
                        </span>
                      )}
                    </div>
                  </div>

                  {assignment.justification && (
                    <div style={{ marginBottom: 15, padding: 12, background: '#F9FAFB', borderRadius: 6 }}>
                      <div className="text-body-sm font-semibold text-text-dark" style={{ marginBottom: 4 }}>Reason</div>
                      <div className="text-body text-text-muted">{assignment.justification}</div>
                    </div>
                  )}

                  <div className="text-body-sm text-text-muted" style={{ display: 'flex', gap: 8, marginBottom: 15 }}>
                    <span>Item Type: <strong>{assignment.itemType}</strong></span>
                    {assignment.estimatedTime && <span>| Est. Time: <strong>{assignment.estimatedTime} min</strong></span>}
                    {assignment.createdAt && <span>| Assigned: <strong>{formatDateTime(assignment.createdAt)}</strong></span>}
                    {assignment.completedAt && <span>| Completed: <strong>{formatDateTime(assignment.completedAt)}</strong></span>}
                  </div>

                  {assignment.declineReason && (
                    <div style={{ marginBottom: 15, padding: 12, background: '#FEE2E2', borderRadius: 6, borderLeft: '3px solid #EF4444' }}>
                      <div className="text-body-sm font-semibold text-red-critical" style={{ marginBottom: 4 }}>Decline Reason</div>
                      <div className="text-body text-red-critical">{assignment.declineReason}</div>
                    </div>
                  )}

                  {viewType === 'assignedToMe' && assignment.status === 'active' && (
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                      <Button
                        variant="primary"
                        size="sm"
                        type="button"
                        onClick={() => handleMarkComplete(assignment.id)}
                      >
                        ✓ Mark Complete
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        type="button"
                        onClick={() => {
                          const reason = prompt('Please provide a reason for declining this assignment:');
                          if (reason) handleDecline(assignment.id, reason);
                        }}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
